import { useMemo } from "react";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import * as AgenticV2API from "@/services/api/agentic-v2";
import type { FaClientDetail, MissionEntityRef } from "@/services/api/agentic-v2";
import { useCompanyId } from "@/context/CompanyContext";
import {
  mergeMissionEntityRefs,
  missionEntitiesMissingDisplayName,
} from "@/components/missions/entityRefs";
import { clientsKeys } from "@/services/api/agentic-v2/queries/keys";

const CLIENT_NAME_STALE_MS = 5 * 60 * 1000;

function resolveFromClientCache(
  queryClient: ReturnType<typeof useQueryClient>,
  entities: MissionEntityRef[],
  companyId?: string,
): MissionEntityRef[] {
  return entities.map((entity) => {
    if (entity.display_name?.trim() || !entity.entity_id) return entity;
    const cached = queryClient.getQueryData<FaClientDetail>(
      clientsKeys.detail(entity.entity_id, companyId),
    );
    if (!cached?.display_name?.trim()) return entity;
    return { ...entity, display_name: cached.display_name };
  });
}

/**
 * Ensure mission entity pills show household names, not raw ids.
 * Uses TanStack client-detail cache first, then fetches missing fa_client rows.
 */
export function useResolvedMissionEntities(
  entities: MissionEntityRef[],
): MissionEntityRef[] {
  const queryClient = useQueryClient();
  const companyId = useCompanyId();
  const fromCache = useMemo(
    () => resolveFromClientCache(queryClient, entities, companyId ?? undefined),
    [queryClient, entities, companyId],
  );

  const idsToFetch = useMemo(
    () => missionEntitiesMissingDisplayName(fromCache),
    [fromCache],
  );

  const clientQueries = useQueries({
    queries: idsToFetch.map((faClientId) => ({
      queryKey: clientsKeys.detail(faClientId, companyId ?? undefined),
      queryFn: () => AgenticV2API.getClient(faClientId),
      staleTime: CLIENT_NAME_STALE_MS,
      enabled: Boolean(faClientId),
    })),
  });

  return useMemo(() => {
    const fetched = fromCache.map((entity) => ({ ...entity }));
    idsToFetch.forEach((faClientId, index) => {
      const name = clientQueries[index]?.data?.display_name?.trim();
      if (!name) return;
      const row = fetched.find((entry) => entry.entity_id === faClientId);
      if (row && !row.display_name?.trim()) {
        row.display_name = name;
      }
    });
    return mergeMissionEntityRefs(entities, fetched);
  }, [clientQueries, entities, fromCache, idsToFetch]);
}
